import { db } from '../firebase';
import { collection, addDoc, Timestamp, writeBatch, doc } from 'firebase/firestore';

// Generate alerts with different severity levels and types
const generateAlerts = async () => {
  const alertsCollection = collection(db, 'alerts');
  const batch = writeBatch(db);
  
  const alerts = [
    {
      id: 'ALERT-001',
      title: 'Potential Data Exfiltration',
      description: 'Unusual outbound data transfer of 2.3GB to an external IP (185.132.45.23) from Finance department workstation.',
      severity: 'high',
      status: 'open',
      source: 'Network Monitoring',
      sourceIp: '10.45.12.87',
      destinationIp: '185.132.45.23',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 3600000 * 2)), // 2 hours ago
      assignedTo: 'security-team',
      tags: ['data-exfiltration', 'finance-department'],
      details: {
        bytesTransferred: 2371234567,
        protocol: 'HTTPS',
        destinationCountry: 'Russia',
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
      }
    },
    {
      id: 'ALERT-002',
      title: 'Brute Force Authentication Attempt',
      description: 'Multiple failed login attempts (27) for admin user from external IP.',
      severity: 'high',
      status: 'investigating',
      source: 'Identity Provider',
      sourceIp: '45.123.67.89',
      destinationIp: '10.0.0.15',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 3600000 * 5)), // 5 hours ago
      assignedTo: 'john.smith',
      tags: ['brute-force', 'authentication', 'admin-account'],
      details: {
        username: 'admin@example.com',
        failedAttempts: 27,
        timeWindow: '15 minutes',
        loginLocation: 'Shanghai, China',
        ipReputation: 'Suspicious'
      }
    },
    {
      id: 'ALERT-003',
      title: 'Suspicious PowerShell Execution',
      description: 'PowerShell script with Base64 encoded commands executed on workstation ENG-WS-045.',
      severity: 'medium',
      status: 'open',
      source: 'Endpoint Security',
      sourceIp: '10.45.16.45',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 3600000 * 12)), // 12 hours ago
      assignedTo: null,
      tags: ['powershell', 'suspicious-execution', 'endpoint'],
      details: {
        hostname: 'ENG-WS-045',
        userName: 'mike.developer',
        commandPattern: 'PowerShell -Encoded UwB0AGEAcgB0AC0...',
        processTree: 'explorer.exe > cmd.exe > powershell.exe',
        filesTouched: ['C:\\Windows\\Temp\\log.txt', 'C:\\Users\\mike.developer\\AppData\\Local\\Temp\\svc.exe']
      }
    },
    {
      id: 'ALERT-004',
      title: 'Malware Detection',
      description: 'Trojan detected and quarantined on HR-WS-012 workstation.',
      severity: 'high',
      status: 'contained',
      source: 'Antivirus',
      sourceIp: '10.45.22.12',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 3600000 * 8)), // 8 hours ago
      assignedTo: 'emma.security',
      tags: ['malware', 'trojan', 'quarantined'],
      details: {
        malwareName: 'Trojan.GenericKD.45762811',
        filePath: 'C:\\Users\\sarah.hr\\Downloads\\Invoice_December_2023.pdf.exe',
        hashSHA256: 'a5e6f0b5c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4',
        detectionEngine: 'Static Analysis + Behavior Monitoring',
        action: 'Quarantined'
      }
    },
    {
      id: 'ALERT-005',
      title: 'Suspicious Authentication',
      description: 'Login from unusual location for user jsmith@example.com',
      severity: 'medium',
      status: 'investigating',
      source: 'Cloud Access Security Broker',
      sourceIp: '104.28.76.192',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 3600000 * 4)), // 4 hours ago
      assignedTo: 'security-team',
      tags: ['unusual-access', 'authentication', 'user-behavior'],
      details: {
        userName: 'jsmith@example.com',
        normalLocation: 'New York, USA',
        currentLocation: 'Bucharest, Romania',
        deviceType: 'Unknown Windows Device',
        accessedResource: 'Financial-Reports-Q4-2023'
      }
    },
    {
      id: 'ALERT-006',
      title: 'Unusual Software Installation',
      description: 'Unapproved software (FileZilla) installed on developer workstation DEV-WS-042.',
      severity: 'low',
      status: 'closed',
      source: 'Endpoint Management',
      sourceIp: '10.45.32.42',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 86400000)), // 1 day ago
      assignedTo: 'ryan.admin',
      tags: ['software-installation', 'policy-violation', 'developers'],
      details: {
        softwareName: 'FileZilla 3.65.0',
        installerHash: '5a4e3c2b1a9e8d7c6b5a4e3c2d1b0a9f8e7d6c5b4a3f2e1d0c9b8a7f6e5d4c3b2a1',
        userName: 'alex.developer',
        department: 'Engineering',
        justificationProvided: 'Needed for code deployment to test environment'
      }
    },
    {
      id: 'ALERT-007',
      title: 'Privilege Escalation',
      description: 'User account assigned to admin role without approval process.',
      severity: 'high',
      status: 'investigating',
      source: 'Identity & Access Management',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 3600000 * 6)), // 6 hours ago
      assignedTo: 'emma.security',
      tags: ['privilege-escalation', 'iam', 'unauthorized-change'],
      details: {
        userName: 'robert.intern',
        newPermissions: 'Admin role for CI/CD pipeline',
        changedBy: 'david.manager',
        approvalReference: 'None',
        auditLogId: 'IAM-20231207-45629'
      }
    },
    {
      id: 'ALERT-008',
      title: 'Cloud Storage Access Alert',
      description: 'Access to sensitive cloud storage bucket during maintenance window.',
      severity: 'low',
      status: 'closed',
      source: 'Cloud Security',
      sourceIp: '10.45.2.23',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 86400000)), // 1 day ago
      assignedTo: 'security-team',
      tags: ['cloud-storage', 'authorized-access', 'maintenance'],
      details: {
        userName: 'james.admin',
        bucketName: 'finance-reports-q3-2023',
        accessType: 'READ',
        changeManagementRef: 'CM-2023-0458',
        maintenanceWindow: 'Dec 7, 2023, 22:00-23:30 UTC'
      }
    },
    {
      id: 'ALERT-009',
      title: 'Phishing Email Detected',
      description: 'Potential phishing email targeting finance department.',
      severity: 'medium',
      status: 'investigating',
      source: 'Email Security Gateway',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 3600000 * 3)), // 3 hours ago
      assignedTo: null,
      tags: ['phishing', 'email', 'finance-department'],
      details: {
        senderEmail: 'accounts@payment-invoicing-services.com',
        senderDomain: 'payment-invoicing-services.com',
        domainAge: '2 days',
        subject: 'Urgent: Update your payment information',
        recipients: ['finance@example.com', 'accounts@example.com'],
        containedLinks: ['https://payment-invoicing-update.com/portal'],
        containedAttachments: ['invoice_details.docx'],
        spfAuthentication: 'Failed'
      }
    },
    {
      id: 'ALERT-010',
      title: 'Unauthorized Configuration Change',
      description: 'Firewall rule modified to allow inbound traffic on non-standard port.',
      severity: 'high',
      status: 'open',
      source: 'Change Management',
      sourceIp: '10.45.1.15',
      timestamp: Timestamp.fromDate(new Date(Date.now() - 3600000 * 1)), // 1 hour ago
      assignedTo: null,
      tags: ['firewall', 'configuration-change', 'unauthorized'],
      details: {
        deviceName: 'edge-fw-01',
        ruleModification: 'Allow inbound TCP on port 8443 from Any to 10.45.16.0/24',
        modifiedBy: 'automation-svc',
        approvalStatus: 'No approval found',
        previousConfiguration: 'Deny inbound TCP on port 8443 from Any to 10.45.16.0/24'
      }
    }
  ];
  
  // Add each alert to the batch
  for (let i = 0; i < alerts.length; i++) {
    const alertRef = doc(alertsCollection);
    batch.set(alertRef, alerts[i]);
  }
  
  // Commit the batch
  await batch.commit();
  console.log('Sample alerts added to Firestore');
};

// Generate network traffic logs
const generateNetworkLogs = async () => {
  const logsCollection = collection(db, 'networkLogs');
  const batch = writeBatch(db);
  
  const logs = [];
  
  // Generate 50 random network logs
  for (let i = 0; i < 50; i++) {
    const protocols = ['HTTP', 'HTTPS', 'DNS', 'SMB', 'RDP', 'SSH', 'FTP', 'SMTP'];
    const internalIPs = ['10.45.12.87', '10.45.16.45', '10.45.22.12', '10.45.32.42', '10.45.2.23', '10.45.1.15'];
    const externalIPs = ['52.34.56.78', '104.28.76.192', '185.132.45.23', '45.123.67.89', '18.45.23.67', '209.58.147.12'];
    const ports = [80, 443, 53, 22, 21, 25, 3389, 445];
    const actions = ['allowed', 'allowed', 'allowed', 'allowed', 'blocked']; // 80% allowed, 20% blocked
    
    const timestamp = new Date();
    timestamp.setHours(timestamp.getHours() - Math.floor(Math.random() * 24)); // Random time in last 24 hours
    
    const log = {
      sourceIp: Math.random() > 0.3 ? internalIPs[Math.floor(Math.random() * internalIPs.length)] : externalIPs[Math.floor(Math.random() * externalIPs.length)],
      destinationIp: Math.random() > 0.7 ? internalIPs[Math.floor(Math.random() * internalIPs.length)] : externalIPs[Math.floor(Math.random() * externalIPs.length)],
      sourcePort: Math.floor(Math.random() * 60000) + 1024,
      destinationPort: ports[Math.floor(Math.random() * ports.length)],
      protocol: protocols[Math.floor(Math.random() * protocols.length)],
      bytesTransferred: Math.floor(Math.random() * 10000000),
      action: actions[Math.floor(Math.random() * actions.length)],
      timestamp: Timestamp.fromDate(timestamp),
      deviceName: 'edge-fw-01'
    };
    
    logs.push(log);
  }
  
  // Add each log to the batch
  for (let i = 0; i < logs.length; i++) {
    const logRef = doc(logsCollection);
    batch.set(logRef, logs[i]);
  }
  
  // Commit the batch
  await batch.commit();
  console.log('Sample network logs added to Firestore');
};

// Generate user activities for UEBA
const generateUserActivities = async () => {
  const activitiesCollection = collection(db, 'userActivities');
  const batch = writeBatch(db);
  
  const activities = [];
  
  const users = [
    { email: 'john.smith@example.com', department: 'IT', role: 'System Administrator' },
    { email: 'emma.security@example.com', department: 'Security', role: 'Security Analyst' },
    { email: 'mike.developer@example.com', department: 'Engineering', role: 'Developer' },
    { email: 'sarah.hr@example.com', department: 'HR', role: 'HR Manager' },
    { email: 'david.manager@example.com', department: 'Engineering', role: 'Engineering Manager' },
    { email: 'robert.intern@example.com', department: 'Engineering', role: 'Intern' }
  ];
  
  const activityTypes = ['login', 'logout', 'file_access', 'admin_action', 'password_change', 'api_access', 'data_export'];
  const locations = ['New York, USA', 'San Francisco, USA', 'London, UK', 'Mumbai, India', 'Singapore', 'Sydney, Australia'];
  const resources = ['ci-cd-pipeline', 'customer-database', 'financial-reports', 'hr-system', 'source-code-repo', 'production-servers'];
  
  // Generate 100 user activities
  for (let i = 0; i < 100; i++) {
    const user = users[Math.floor(Math.random() * users.length)];
    const activityType = activityTypes[Math.floor(Math.random() * activityTypes.length)];
    
    const timestamp = new Date();
    timestamp.setHours(timestamp.getHours() - Math.floor(Math.random() * 72)); // Random time in last 72 hours
    
    let ipAddress;
    let location;
    
    // 90% of activities from expected locations, 10% anomalous
    if (Math.random() > 0.1) {
      ipAddress = '10.45.' + Math.floor(Math.random() * 255) + '.' + Math.floor(Math.random() * 255);
      location = 'New York, USA'; // Default company location
    } else {
      // Random external IP
      ipAddress = Math.floor(Math.random() * 255) + '.' + Math.floor(Math.random() * 255) + '.' + Math.floor(Math.random() * 255) + '.' + Math.floor(Math.random() * 255);
      location = locations[Math.floor(Math.random() * locations.length)];
    }
    
    const activity = {
      userId: user.email,
      userName: user.email.split('@')[0],
      department: user.department,
      role: user.role,
      activityType: activityType,
      ipAddress: ipAddress,
      location: location,
      timestamp: Timestamp.fromDate(timestamp),
      device: Math.random() > 0.7 ? 'Mobile' : 'Desktop',
      browser: Math.random() > 0.3 ? 'Chrome' : Math.random() > 0.5 ? 'Firefox' : 'Safari',
      successful: Math.random() > 0.05, // 95% success rate
      resource: activityType === 'file_access' || activityType === 'api_access' ? resources[Math.floor(Math.random() * resources.length)] : null,
      details: {}
    };
    
    // Add activity-specific details
    switch (activityType) {
      case 'login':
        activity.details = {
          authMethod: Math.random() > 0.3 ? 'Password' : 'MFA',
          firstLogin: Math.random() > 0.9
        };
        break;
      case 'file_access':
        activity.details = {
          fileName: 'document-' + Math.floor(Math.random() * 1000) + '.pdf',
          accessType: Math.random() > 0.3 ? 'Read' : 'Write',
          sensitivityLevel: Math.random() > 0.7 ? 'High' : Math.random() > 0.5 ? 'Medium' : 'Low'
        };
        break;
      case 'admin_action':
        activity.details = {
          action: Math.random() > 0.5 ? 'User Permission Change' : Math.random() > 0.5 ? 'System Configuration' : 'Security Policy Update',
          target: users[Math.floor(Math.random() * users.length)].email,
          justification: Math.random() > 0.8 ? null : 'Requested by management'
        };
        break;
      case 'data_export':
        activity.details = {
          dataType: Math.random() > 0.7 ? 'Customer Data' : Math.random() > 0.5 ? 'Financial Data' : 'System Logs',
          recordCount: Math.floor(Math.random() * 10000),
          format: Math.random() > 0.6 ? 'CSV' : Math.random() > 0.5 ? 'Excel' : 'JSON'
        };
        break;
    }
    
    activities.push(activity);
  }
  
  // Add each activity to the batch
  for (let i = 0; i < activities.length; i++) {
    const activityRef = doc(activitiesCollection);
    batch.set(activityRef, activities[i]);
  }
  
  // Commit the batch
  await batch.commit();
  console.log('Sample user activities added to Firestore');
};

// Generate vulnerability data
const generateVulnerabilities = async () => {
  const vulnerabilitiesCollection = collection(db, 'vulnerabilities');
  const batch = writeBatch(db);
  
  const vulnerabilities = [
    {
      id: 'CVE-2023-12345',
      title: 'Critical SQL Injection Vulnerability in Internal Application',
      description: 'A SQL injection vulnerability exists in the search functionality of the internal customer management application.',
      severity: 'critical',
      cvssScore: 9.8,
      status: 'open',
      discoveredDate: Timestamp.fromDate(new Date(Date.now() - 86400000 * 5)), // 5 days ago
      affectedSystems: ['app-server-01', 'app-server-02', 'app-server-03'],
      remediation: 'Apply parameter sanitization and prepared statements in the search.php module. Patch available in version 3.5.2.',
      assignedTo: 'mike.developer@example.com',
      exploitAvailable: true,
      patchAvailable: true,
      tags: ['injection', 'web-app', 'critical']
    },
    {
      id: 'CVE-2023-54321',
      title: 'Outdated TLS Version on Web Servers',
      description: 'External-facing web servers are using TLS 1.0/1.1 which is deprecated and contains known vulnerabilities.',
      severity: 'medium',
      cvssScore: 5.6,
      status: 'in_progress',
      discoveredDate: Timestamp.fromDate(new Date(Date.now() - 86400000 * 12)), // 12 days ago
      affectedSystems: ['web-server-01', 'web-server-02', 'web-server-03', 'web-server-04'],
      remediation: 'Disable TLS 1.0/1.1 and configure servers to only use TLS 1.2 or higher. Update cipher suites to modern standards.',
      assignedTo: 'john.smith@example.com',
      exploitAvailable: false,
      patchAvailable: true,
      tags: ['encryption', 'compliance', 'web-servers']
    },
    {
      id: 'VULN-2023-789',
      title: 'Unpatched Linux Kernel Vulnerability',
      description: 'Server systems running Linux kernel version 5.4.0-81 are vulnerable to privilege escalation.',
      severity: 'high',
      cvssScore: 7.8,
      status: 'open',
      discoveredDate: Timestamp.fromDate(new Date(Date.now() - 86400000 * 3)), // 3 days ago
      affectedSystems: ['db-server-01', 'db-server-02', 'app-server-04', 'app-server-05'],
      remediation: 'Update Linux kernel to version 5.4.0-90 or later which contains the security patch.',
      assignedTo: null,
      exploitAvailable: true,
      patchAvailable: true,
      tags: ['privilege-escalation', 'linux', 'kernel']
    },
    {
      id: 'VULN-2023-456',
      title: 'Default Credentials in Network Equipment',
      description: 'Multiple network devices were found with default or weak administrative credentials.',
      severity: 'high',
      cvssScore: 8.1,
      status: 'in_progress',
      discoveredDate: Timestamp.fromDate(new Date(Date.now() - 86400000 * 7)), // 7 days ago
      affectedSystems: ['switch-01', 'switch-03', 'router-02', 'firewall-edge-02'],
      remediation: 'Change all administrative passwords to complex values according to the company password policy. Implement password management system.',
      assignedTo: 'emma.security@example.com',
      exploitAvailable: true,
      patchAvailable: false,
      tags: ['default-credentials', 'network-devices', 'authentication']
    },
    {
      id: 'VULN-2023-333',
      title: 'Misconfigured S3 Bucket',
      description: 'Public read access enabled on an S3 bucket containing system backups.',
      severity: 'high',
      cvssScore: 7.5,
      status: 'open',
      discoveredDate: Timestamp.fromDate(new Date(Date.now() - 86400000 * 1)), // 1 day ago
      affectedSystems: ['aws-s3-backups'],
      remediation: 'Reconfigure bucket permissions to remove public access. Implement proper IAM controls and bucket policies.',
      assignedTo: null,
      exploitAvailable: false,
      patchAvailable: false,
      tags: ['cloud', 'misconfiguration', 'aws', 'data-exposure']
    },
    {
      id: 'VULN-2023-222',
      title: 'Insecure CORS Configuration in API',
      description: 'The company API has a wildcard CORS policy allowing requests from any origin.',
      severity: 'medium',
      cvssScore: 6.5,
      status: 'open',
      discoveredDate: Timestamp.fromDate(new Date(Date.now() - 86400000 * 2)), // 2 days ago
      affectedSystems: ['api-gateway', 'api-server-01', 'api-server-02'],
      remediation: 'Update CORS policy to explicitly allow only trusted domains. Remove wildcard origin.',
      assignedTo: 'mike.developer@example.com',
      exploitAvailable: false,
      patchAvailable: true,
      tags: ['api', 'web-security', 'cors']
    },
    {
      id: 'VULN-2023-111',
      title: 'Unpatched Exchange Server',
      description: 'Exchange server missing critical security patches released in November 2023.',
      severity: 'critical',
      cvssScore: 9.6,
      status: 'in_progress',
      discoveredDate: Timestamp.fromDate(new Date(Date.now() - 86400000 * 8)), // 8 days ago
      affectedSystems: ['mail-server-01'],
      remediation: 'Apply Microsoft Security Update KB5032204 and all other missing patches.',
      assignedTo: 'john.smith@example.com',
      exploitAvailable: true,
      patchAvailable: true,
      tags: ['microsoft', 'email', 'unpatched', 'critical']
    },
    {
      id: 'VULN-2023-987',
      title: 'Insecure Direct Object References in Web Application',
      description: 'The customer portal allows for direct access to objects without proper authorization checks.',
      severity: 'high',
      cvssScore: 7.3,
      status: 'open',
      discoveredDate: Timestamp.fromDate(new Date(Date.now() - 86400000 * 4)), // 4 days ago
      affectedSystems: ['web-app-customer-portal'],
      remediation: 'Implement proper access control checks for all object references. Use indirect references mapped to actual database identifiers.',
      assignedTo: null,
      exploitAvailable: false,
      patchAvailable: false,
      tags: ['web-app', 'access-control', 'authorization']
    }
  ];
  
  // Add each vulnerability to the batch
  for (let i = 0; i < vulnerabilities.length; i++) {
    const vulnRef = doc(vulnerabilitiesCollection);
    batch.set(vulnRef, vulnerabilities[i]);
  }
  
  // Commit the batch
  await batch.commit();
  console.log('Sample vulnerabilities added to Firestore');
};

// Main function to populate all sample data
const populateSampleData = async () => {
  try {
    await generateAlerts();
    await generateNetworkLogs();
    await generateUserActivities();
    await generateVulnerabilities();
    console.log('All sample data successfully added to Firestore');
    return true;
  } catch (error) {
    console.error('Error populating sample data:', error);
    return false;
  }
};

export { populateSampleData }; 